import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {LayoutDashboard, Building2, Users, Briefcase, Star, AlertTriangle, BookOpen, Bell, Settings, LogOut, Menu, Shield, X, ClipboardList, GraduationCap, SlidersHorizontal} from 'lucide-react'

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', to: '/admin/dashboard' },
  { icon: Building2, label: 'Empresas', to: '/admin/empresas' },
  { icon: Users, label: 'Colaboradores', to: '/admin/colaboradores' },
  { icon: Briefcase, label: 'Vacantes', to: '/admin/vacantes' },
  { icon: Star, label: 'Evaluaciones', to: '/admin/evaluaciones' },
  { icon: SlidersHorizontal, label: 'Aspectos por empresa', to: '/admin/aspectos-evaluacion' },
  { icon: ClipboardList, label: 'Gestión de talento', to: '/admin/gestion-talento' },
  { icon: AlertTriangle, label: 'Casos y reportes', to: '/admin/casos' },
  { icon: GraduationCap, label: 'Cursos', to: '/admin/cursos' },
  { icon: BookOpen, label: 'Capacitaciones', to: '/admin/capacitaciones' },
  { icon: Bell, label: 'Notificaciones', to: '/admin/notificaciones' },
  { icon: Settings, label: 'Configuración', to: '/admin/configuracion' },
]

interface AdminLayoutProps {
  title: string
  subtitle?: string
  children: ReactNode
}

function AdminLayout({ title, subtitle, children }: AdminLayoutProps) {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = () => {
    localStorage.removeItem('beeznz_admin')
    window.location.href = '/'
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f8fafc' }}>
      {/* Overlay móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col transition-transform md:static md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: '#0d1421', borderRight: '1px solid rgba(255,255,255,0.07)' }}
      >
        {/* Logo superior */}
        <div className="flex h-16 items-center justify-between px-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <Link to="/admin/dashboard" onClick={() => setSidebarOpen(false)}>
            <img
              src="https://static.lumi.new/93/93deb8f4c6e571fa108d99af77ea2979.webp"
              alt="Beeznz Work"
              className="h-8 w-auto"
            />
          </Link>
          <button
            className="md:hidden text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        {/* Badge administrador */}
        <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
              style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)' }}
            >
              <Shield size={16} className="text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-white leading-tight">Administrador</p>
              <p className="text-xs text-slate-400">Acceso total al sistema</p>
            </div>
          </div>
        </div>

        {/* Navegación */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {navItems.map(({ icon: Icon, label, to }) => {
            const active = location.pathname === to
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Icon size={17} className={active ? 'text-amber-400' : ''} />
                {label}
              </Link>
            )
          })}
        </nav>

        {/* Logo pie + Cerrar sesión */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="px-4 py-4 flex items-center justify-center">
            <img
              src="https://static.lumi.new/d5/d5398fcebcce92c05914f918c9cc339e.webp"
              alt="Powered by Beeznz"
              className="h-10 w-auto opacity-80"
            />
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-5 py-4 text-sm font-semibold transition-colors hover:bg-white/5"
              style={{ color: '#f87171' }}
            >
              <LogOut size={17} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Contenido principal */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Área de contenido */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <div className="mb-6 flex items-start gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden mt-1 rounded-lg p-1.5 text-gray-500 hover:text-gray-900 flex-shrink-0"
            >
              <Menu size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              {subtitle ? <p className="mt-1 text-sm text-gray-500">{subtitle}</p> : null}
            </div>
          </div>
          {children}
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
